import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { PieChart, Pie, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { UserContext } from '../../App';

const API_URL = process.env.REACT_APP_API_URL;
const accessToken = localStorage.getItem("accessToken");

// --- Inline SVG Icons ---
const iconSearch = (
    <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
    </svg>
);
const iconEdit = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a12.007 12.007 0 014.243 4.243l-4.243 4.243M18 10l-6.364 6.364M18 10l-6.364 6.364M18 10l-6.364 6.364" />
        <path d="M15.232 5.232a2.5 2.5 0 013.536 3.536L9 19H5v-4L15.232 5.232z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
    </svg>
);

const iconView = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);
const iconClose = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);
const iconSuccess = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
    </svg>
);
const iconDanger = (
    <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto text-red-500 w-12 h-12 mb-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 9V7a2 2 0 00-2-2h-4a2 2 0 00-2 2v2H5.5a1 1 0 000 2h.5V19a2 2 0 002 2h9a2 2 0 002-2v-8h.5a1 1 0 000-2zM9 7a1 1 0 011-1h4a1 1 0 011 1v2H9z" />
    </svg>
);
const iconPrint = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
    </svg>
);

// --- Helper to format price without decimals ---
const formatPrice = (price) => {
    return Math.round(price);
};

// --- Helper to format price in CDF format (using 2 decimals for precision) ---
const formatCDF = (amount) => {
    // Ensure it's a number before formatting, and use 2 decimal places
    const numericAmount = parseFloat(amount || 0);
    return `${numericAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CDF`;
};

// --- Pricing Calculation Helper (UPDATED: Platform Cut added, TVA REMOVED) ---
const calculateTotalWithFees = (rawSubtotal) => {
    const PLATFORM_CUT_RATE = 0.10; // 10% platform cut
    // TVA removed as requested
    const subtotal = parseFloat(rawSubtotal || 0);

    // 1. Supplier's Net Calculation
    const platformCut = subtotal * PLATFORM_CUT_RATE;
    const supplierTotal = subtotal - platformCut;

    // 2. Customer's Total Calculation (Hors Taxe / HT)
    // Delivery fee logic updated based on user request:
    let deliveryPercentage;
    if (subtotal > 100000) {
        // More than 100.000 delivery is 5%
        deliveryPercentage = 0.08;
    } else if (subtotal >= 45001) {
        // 45001 and 100.000 delivery is 9%
        deliveryPercentage = 0.10;
    } else if (subtotal >= 1) {
        // 1 CDF and 45000 CDF delivery is 15%
        deliveryPercentage = 0.15;
    } else {
        // Handle subtotal <= 0
        deliveryPercentage = 0.00;
    }

    const deliveryFee = subtotal * deliveryPercentage;
    const total = subtotal + deliveryFee; // Customer's Final Total (HT)

    return {
        subtotal: subtotal.toFixed(2),
        platformCut: platformCut.toFixed(2),
        supplierTotal: supplierTotal.toFixed(2), // Supplier's Net
        deliveryFee: deliveryFee.toFixed(2),
        deliveryPercentage: deliveryPercentage * 100,
        // TVA fields are removed
        total: total.toFixed(2) // Customer's Final Total (HT)
    };
};

// --- Custom Modal Component ---
const CustomModal = ({ isOpen, onClose, children, title }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-70 transition-opacity duration-300 ease-out"
            onClick={onClose}>
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto transform transition-all duration-300 ease-out scale-100"
                onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-4">
                    <h2 className="text-xl font-bold text-gray-800">{title}</h2>
                    <button
                        onClick={onClose}
                        className="p-2 -mr-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                        {iconClose}
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
};

// --- View Order Modal (Updated: TVA removed, Total is HT) ---
const ViewOrderModal = ({ isOpen, onClose, order }) => {
    if (!order) return null;

    const statusMap = {
        'pending': 'En attente',
        'in_transit': 'En cours de livraison',
        'delivered': 'Livrée',
        'cancelled': 'Annulée'
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'in_transit':
                return 'bg-blue-100 text-blue-800';
            case 'delivered':
                return 'bg-green-100 text-green-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <CustomModal isOpen={isOpen} onClose={onClose} title={`Détails de la commande #${order.orderId}`}>
            <div className="space-y-4 text-gray-700">
                <div className="flex items-center space-x-2">
                    <p className="font-semibold text-gray-900">Statut:</p>
                    <span className={`inline-block px-3 py-1 mt-1 text-xs font-bold rounded-full ${getStatusStyle(order.status)}`}>
                        {statusMap[order.status]}
                    </span>
                </div>
                <div>
                    <p className="font-semibold text-gray-900">Client:</p>
                    <p>{order.customerFirstName} {order.customerLastName}</p>
                </div>
                <div>
                    <p className="font-semibold text-gray-900">Livreur:</p>
                    <p>{order.deliveryPersonFirstName || 'Non assigné'} {order.deliveryPersonLastName || ''}</p>
                </div>
                <div>
                    <p className="font-semibold text-gray-900">Adresse de livraison:</p>
                    <p>{order.deliveryAddress}</p>
                </div>
                <div>
                    <p className="font-semibold text-gray-900">Articles de la commande:</p>
                    <ul className="list-disc list-inside mt-2 space-y-2">
                        {order.orderItems?.length > 0 ? (
                            order.orderItems.map((item, index) => (
                                <li key={index} className="bg-gray-50 p-3 rounded-lg flex justify-between">
                                    <div>
                                        <span className="font-medium">{item.name}</span>
                                        <p className="text-xs text-gray-500">
                                            Quantité: {item.quantity} | Prix unitaire: {parseFloat(item.priceAtTimeOfOrder).toFixed(2)} CDF/{item.unit || 'unité'}
                                        </p>
                                    </div>
                                    <span className="font-semibold text-gray-900">
                                        {(item.quantity * parseFloat(item.priceAtTimeOfOrder)).toFixed(2)} CDF
                                    </span>
                                </li>
                            ))
                        ) : (
                            <li className="text-sm text-gray-500 text-center">Aucun article dans cette commande.</li>
                        )}
                    </ul>
                </div>

                {/* --- UPDATED TOTALS SECTION (TVA REMOVED) --- */}
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-2 text-gray-700">
                    <div className="flex justify-between text-base">
                        <span className="text-gray-600">Sous-total (Produits):</span>
                        <span className="font-medium">{formatCDF(order.subtotal || 0)}</span>
                    </div>
                    <div className="flex justify-between text-base border-b pb-2">
                        <span className="text-gray-600">Frais de plateforme (10%):</span>
                        <span className="font-medium text-red-600">-{formatCDF(order.platformCut || 0)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-semibold pt-2 text-gray-900 border-b pb-2">
                        <p>Total Fournisseur (Net):</p>
                        <span className="text-green-600">{formatCDF(order.supplierTotal || 0)}</span>
                    </div>
                    <div className="flex justify-between text-base">
                        <span className="text-gray-600">Frais de livraison ({order.deliveryPercentage || 'N/A'}%):</span>
                        <span className="font-medium">+{formatCDF(order.deliveryFee || 0)}</span>
                    </div>
                    {/* TVA row removed */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-300">
                        <p className="text-xl font-bold text-gray-800">Montant Total (Client HT):</p>
                        <span className="text-2xl font-extrabold text-indigo-600">{formatCDF(order.totalAmount || 0)}</span>
                    </div>
                </div>
                {/* --- END UPDATED TOTALS SECTION --- */}

            </div>
        </CustomModal>
    );
};

const OrderFormModal = ({ isOpen, onClose, onSuccess, orderToEdit, currentSupplierId }) => {
    const isEditing = !!orderToEdit;
    const [formData, setFormData] = useState({
        customerId: '',
        deliveryPersonId: '',
        status: 'pending',
        deliveryAddress: ''
    });
    const [customers, setCustomers] = useState([]);
    const [deliveryPeople, setDeliveryPeople] = useState([]);
    const [orderItems, setOrderItems] = useState([]);
    const [formError, setFormError] = useState(null);
    const [loading, setLoading] = useState(false);

    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
    const [deliveryPersonSearchTerm, setDeliveryPersonSearchTerm] = useState('');
    const [isDeliveryPersonDropdownOpen, setIsDeliveryPersonDropdownOpen] = useState(false);

    const customerDropdownRef = useRef(null);
    const deliveryDropdownRef = useRef(null);

    // Calculate subtotal from order items
    const subtotal = orderItems.reduce((sum, item) => sum + (parseFloat(item.priceAtTimeOfOrder) * item.quantity), 0);
    // Assuming calculateTotalWithFees is a defined function
    const pricing = calculateTotalWithFees(subtotal);

    const fetchLists = useCallback(async () => {
        if (!currentSupplierId) {
            setFormError("Supplier ID is missing, cannot load user lists.");
            return;
        }

        setFormError(null);
        try {
            const [customersRes, deliveryPeopleRes, productsRes] = await Promise.all([
                fetch(`${API_URL}/users?user_type_id=2&limit=100`, { headers: { 'Authorization': `Bearer ${accessToken}` } }),
                // Fetch all delivery users, we will filter them below
                fetch(`${API_URL}/delivery-users?limit=100`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                }),
                fetch(`${API_URL}/products`, { headers: { 'Authorization': `Bearer ${accessToken}` } })
            ]);

            if (!customersRes.ok || !deliveryPeopleRes.ok || !productsRes.ok) {
                throw new Error("Failed to fetch user lists or product lists for the form.");
            }

            const customersData = await customersRes.json();
            const deliveryPeopleData = await deliveryPeopleRes.json();

            // --- IMPLEMENTATION OF REQUEST 2: Filter Delivery People by Supplier ID ---
            const filteredDeliveryUsers = (deliveryPeopleData.deliveryUsers || []).filter(
                user => user.userIdBelongTo === currentSupplierId
            );

            setCustomers(customersData.users || []);
            setDeliveryPeople(filteredDeliveryUsers); // Set the filtered list
            // -----------------------------------------------------------------------

        } catch (err) {
            console.error("💥 Erreur dans la fonction fetchLists:", err);
            setFormError("Failed to load user and product lists for the form.");
        }
    }, [currentSupplierId]); // Dependency added for filtering

    useEffect(() => {
        if (isOpen) {
            fetchLists();
            if (isEditing && orderToEdit) {
                setLoading(true);
                setFormData({
                    customerId: orderToEdit.customerId || '',
                    deliveryPersonId: orderToEdit.deliveryPersonId || '',
                    status: orderToEdit.status || 'pending',
                    deliveryAddress: orderToEdit.deliveryAddress || ''
                });
                setOrderItems(orderToEdit.orderItems || []);

                // --- IMPLEMENTATION OF REQUEST 1: Auto-select/Auto-fill search terms ---
                // Customer Auto-Select
                if (orderToEdit.customerName) {
                    setCustomerSearchTerm(orderToEdit.customerName);
                } else if (orderToEdit.customerId && customers.length > 0) {
                    const customer = customers.find(c => c.userId === orderToEdit.customerId);
                    if (customer) setCustomerSearchTerm(`${customer.firstName} ${customer.lastName}`);
                }

                // Delivery Person Auto-Select
                if (orderToEdit.deliveryPersonName) {
                    setDeliveryPersonSearchTerm(orderToEdit.deliveryPersonName);
                } else if (orderToEdit.deliveryPersonId && deliveryPeople.length > 0) {
                    const person = deliveryPeople.find(d => d.userId === orderToEdit.deliveryPersonId);
                    if (person) setDeliveryPersonSearchTerm(`${person.firstName} ${person.lastName}`);
                }
                // -----------------------------------------------------------------------

                setLoading(false);
            } else {
                setFormData({ customerId: '', deliveryPersonId: '', status: 'pending', deliveryAddress: '' });
                setOrderItems([]);
                setCustomerSearchTerm('');
                setDeliveryPersonSearchTerm('');
            }
        }
    }, [isOpen, isEditing, orderToEdit, fetchLists, customers, deliveryPeople]); // Added dependencies

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
                setIsCustomerDropdownOpen(false);
            }
            if (deliveryDropdownRef.current && !deliveryDropdownRef.current.contains(event.target)) {
                setIsDeliveryPersonDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFormError(null);

        if (!formData.customerId) {
            setFormError("Veuillez sélectionner un client dans la liste.");
            setLoading(false);
            return;
        }

        if (orderItems.length === 0) {
            setFormError("La commande doit contenir au moins un article.");
            setLoading(false);
            return;
        }

        // Auto-assign delivery person if not selected (uses the already filtered deliveryPeople list)
        let deliveryPersonId = formData.deliveryPersonId;
        if (!deliveryPersonId && deliveryPeople.length > 0) {
            const randomIndex = Math.floor(Math.random() * deliveryPeople.length);
            deliveryPersonId = deliveryPeople[randomIndex].userId;
        }

        const orderItemPayload = orderItems.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            priceAtTimeOfOrder: parseFloat(item.priceAtTimeOfOrder)
        }));

        const payload = {
            ...formData,
            deliveryPersonId,
            subtotal: pricing.subtotal,
            platformCut: pricing.platformCut,
            supplierTotal: pricing.supplierTotal,
            deliveryFee: pricing.deliveryFee,
            deliveryPercentage: pricing.deliveryPercentage,
            totalAmount: pricing.total,
            orderItems: orderItemPayload,
            paymentMethod: 'cash',
            supplierId: currentSupplierId // Ensure the order is linked to the current supplier
        };

        try {
            const endpoint = isEditing ? `${API_URL}/orders/${orderToEdit.orderId}` : `${API_URL}/orders`;
            const method = isEditing ? 'PUT' : 'POST';

            const res = await fetch(endpoint, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || `Failed to ${isEditing ? 'update' : 'add'} order.`);
            }

            const data = await res.json();
            onSuccess(data);
            onClose();

        } catch (err) {
            setFormError(err.message || 'An error occurred.');
            console.error("Error during order submission:", err);
        } finally {
            setLoading(false);
        }
    };

    const filteredCustomers = customers.filter(c =>
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(customerSearchTerm.toLowerCase())
    );
    // deliveryPeople is already filtered by currentSupplierId in fetchLists
    const filteredDeliveryPeople = deliveryPeople.filter(d =>
        `${d.firstName} ${d.lastName}`.toLowerCase().includes(deliveryPersonSearchTerm.toLowerCase())
    );

    const handleCustomerSelect = (customer) => {
        setFormData(prev => ({ ...prev, customerId: customer.userId }));
        setCustomerSearchTerm(`${customer.firstName} ${customer.lastName}`);
        setIsCustomerDropdownOpen(false);
    };

    const handleDeliveryPersonSelect = (person) => {
        setFormData(prev => ({ ...prev, deliveryPersonId: person.userId }));
        setDeliveryPersonSearchTerm(`${person.firstName} ${person.lastName}`);
        setIsDeliveryPersonDropdownOpen(false);
    };

    if (loading && isEditing) {
        // Assuming CustomModal is imported or defined
        return (
            <CustomModal isOpen={isOpen} onClose={onClose} title="Loading Order Details">
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
                </div>
            </CustomModal>
        )
    }

    return (
        <CustomModal isOpen={isOpen} onClose={onClose} title={isEditing ? "Modifier la commande" : "Créer une commande"}>
            {formError && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded-lg" role="alert">
                    <p className="font-bold">Erreur</p>
                    <p>{formError}</p>
                </div>
            )}
            <form onSubmit={handleSubmit}>
                <div className="flex flex-col mb-4 relative" ref={customerDropdownRef}>
                    <label className="text-sm font-medium text-gray-700 mb-1" htmlFor="customer-search">Client</label>
                    <input
                        type="text"
                        id="customer-search"
                        value={customerSearchTerm}
                        onChange={(e) => {
                            setCustomerSearchTerm(e.target.value);
                            setIsCustomerDropdownOpen(true);
                            // Clear ID when user starts typing new search
                            setFormData(prev => ({ ...prev, customerId: '' }));
                        }}
                        onFocus={() => setIsCustomerDropdownOpen(true)}
                        placeholder="Rechercher un client..."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        required
                    />
                    {isCustomerDropdownOpen && (
                        <ul className="absolute z-10 w-full mt-12 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {filteredCustomers.length > 0 ? (
                                filteredCustomers.map(c => (
                                    <li
                                        key={c.userId}
                                        onClick={() => handleCustomerSelect(c)}
                                        className="p-3 cursor-pointer hover:bg-indigo-100 transition-colors"
                                    >
                                        {c.firstName} {c.lastName} ({c.email})
                                    </li>
                                ))
                            ) : (
                                <li className="p-3 text-gray-500">Aucun client trouvé.</li>
                            )}
                        </ul>
                    )}
                </div>

                <div className="flex flex-col mb-4 relative" ref={deliveryDropdownRef}>
                    <label className="text-sm font-medium text-gray-700 mb-1" htmlFor="delivery-person-search">
                        Livreur (optionnel - auto-assigné si vide)
                    </label>
                    <input
                        type="text"
                        id="delivery-person-search"
                        value={deliveryPersonSearchTerm}
                        onChange={(e) => {
                            setDeliveryPersonSearchTerm(e.target.value);
                            setIsDeliveryPersonDropdownOpen(true);
                            // Clear ID when user starts typing new search
                            setFormData(prev => ({ ...prev, deliveryPersonId: '' }));
                        }}
                        onFocus={() => setIsDeliveryPersonDropdownOpen(true)}
                        placeholder="Rechercher un livreur..."
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                    {isDeliveryPersonDropdownOpen && (
                        <ul className="absolute z-10 w-full mt-12 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {filteredDeliveryPeople.length > 0 ? (
                                filteredDeliveryPeople.map(d => (
                                    <li
                                        key={d.userId}
                                        onClick={() => handleDeliveryPersonSelect(d)}
                                        className="p-3 cursor-pointer hover:bg-indigo-100 transition-colors"
                                    >
                                        {d.firstName} {d.lastName}
                                    </li>
                                ))
                            ) : (
                                <li className="p-3 text-gray-500">
                                    Aucun livreur trouvé pour ce fournisseur.
                                </li>
                            )}
                        </ul>
                    )}
                </div>

                <div className="flex flex-col mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1" htmlFor="status">Statut</label>
                    <select
                        name="status"
                        id="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        required
                    >
                        <option value="pending">En attente</option>
                        <option value="in_transit">En cours de livraison</option>
                        <option value="delivered">Livrée</option>
                        <option value="cancelled">Annulée</option>
                    </select>
                </div>
                <div className="flex flex-col mb-6">
                    <label className="text-sm font-medium text-gray-700 mb-1" htmlFor="deliveryAddress">Adresse de livraison</label>
                    <textarea
                        name="deliveryAddress"
                        id="deliveryAddress"
                        value={formData.deliveryAddress}
                        onChange={handleChange}
                        placeholder="Adresse de livraison complète"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        rows="2"
                        required
                    />
                </div>
                <div className="flex justify-end space-x-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                        disabled={loading}
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        className="px-6 py-3 bg-indigo-500 rounded-lg text-white font-semibold hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        {loading ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Créer')}
                    </button>
                </div>
            </form>
        </CustomModal>
    );
};


// --- New Component for Analytics Dashboard ---
const OrdersDashboard = ({ orders }) => {
    const statusData = [
        { name: 'En attente', value: orders.filter(o => o.status === 'pending').length },
        { name: 'En cours', value: orders.filter(o => o.status === 'in_transit').length },
        { name: 'Livrée', value: orders.filter(o => o.status === 'delivered').length },
        { name: 'Annulée', value: orders.filter(o => o.status === 'cancelled').length },
    ];

    const monthlySalesData = orders.reduce((acc, order) => {
        const date = new Date(order.createdAt);
        const month = date.toLocaleString('fr-FR', { month: 'long', year: 'numeric' });

        // Use the supplierTotal for accurate sales tracking
        const salesAmount = parseFloat(order.supplierTotal || order.supplierTotalAmount || 0);

        const existingMonth = acc.find(item => item.name === month);
        if (existingMonth) {
            existingMonth.sales += salesAmount;
        } else {
            acc.push({ name: month, sales: salesAmount });
        }
        return acc;
    }, []);

    const COLORS = ['#FFBB28', '#0088FE', '#00C49F', '#FF8042'];

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Statut de mes commandes</h2>
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={statusData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            fill="#8884d8"
                            label={(entry) => `${entry.name} (${entry.value})`}
                        >
                            {statusData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Montant total des ventes par mois</h2>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlySalesData}>
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={(value) => formatPrice(value)} />
                        <Tooltip formatter={(value) => formatCDF(value)} />
                        <Legend />
                        <Bar dataKey="sales" fill="#8884d8" name="Total Ventes (Net Fournisseur)" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

// --- Main Page Component (Updated for Supplier) ---
const OrdersPage = () => {
    const { user } = useContext(UserContext);
    const [inputTerm, setInputTerm] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
       const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);
    const [messageType, setMessageType] = useState('success');

    const [orders, setOrders] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isFetchingDetailedOrder, setIsFetchingDetailedOrder] = useState(false);
    const ordersPerPage = 10;

    const fetchOrders = useCallback(async () => {
        if (!user) return;

        setLoading(true);
        try {
            const query = new URLSearchParams({
                q: searchTerm,
                page: page,
                limit: ordersPerPage
            }).toString();

            const url = `${API_URL}/suppliers/${user.userId}/orders?${query}`;
            const res = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (!res.ok) {
                throw new Error("Échec de la récupération des commandes.");
            }

            const data = await res.json();
            console.log('data', data);

            // Calculate platformCut and supplierTotal for each order
            const ordersWithCalculations = data.orders.map(order => {
                let rawSubtotal = 0;

                // 1. Calculate the raw subtotal from order items
                if (order.orderItems && order.orderItems.length > 0) {
                    rawSubtotal = order.orderItems.reduce((sum, item) => {
                        return sum + (parseFloat(item.priceAtTimeOfOrder || 0) * parseFloat(item.quantity || 0));
                    }, 0);
                } else if (order.totalAmount) {
                    // Fallback logic for rawSubtotal from totalAmount (ADJUSTED TO REMOVE TVA)
                    const total = parseFloat(order.totalAmount); // Now assumed to be HT
                    const deliveryFeeFromOrder = parseFloat(order.deliveryFee || 0); // If provided by backend

                    if (deliveryFeeFromOrder > 0) {
                        rawSubtotal = total - deliveryFeeFromOrder;
                    } else {
                        // Estimate raw subtotal assuming delivery fee logic (6% or 10%)
                        // The previous logic divided by 1.16 for TVA, which is now removed.
                        const subtotalWith10Delivery = total / 1.10;
                        const subtotalWith6Delivery = total / 1.06;

                        rawSubtotal = subtotalWith10Delivery < 100000 ? subtotalWith10Delivery : subtotalWith6Delivery;
                    }
                }

                // Calculate all pricing using the new consolidated helper
                const pricing = calculateTotalWithFees(rawSubtotal);

                console.log('Order calculations:', {
                    orderId: order.orderId,
                    rawSubtotal,
                    ...pricing
                });

                return {
                    ...order,
                    // Attach all new calculated fields (now strings with 2 decimals)
                    subtotal: pricing.subtotal,
                    platformCut: pricing.platformCut,
                    supplierTotal: pricing.supplierTotal, // Supplier's Net
                    deliveryFee: pricing.deliveryFee,
                    deliveryPercentage: pricing.deliveryPercentage,
                    // TVA field is removed
                    totalAmount: pricing.total, // Customer's Final Total (HT)

                    // Keep supplierTotalAmount for backwards compatibility where it's used as the rounded net total
                    supplierTotalAmount: pricing.supplierTotal,
                };
            });

            setOrders(ordersWithCalculations);
            setTotalCount(data.totalCount);
        } catch (err) {
            console.error(err);
            showMessage("Une erreur est survenue lors du chargement des données.", 'error');
        } finally {
            setLoading(false);
        }
    }, [user, searchTerm, page]);

    const fetchDetailedOrder = useCallback(async (orderId) => {
        setIsFetchingDetailedOrder(true);
        try {
            const res = await fetch(`${API_URL}/suppliers/${user.userId}/orders/${orderId}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || "Failed to fetch order details.");
            }

            const data = await res.json();
            const detailedOrder = data.order;

            // Re-run the calculation logic on the detailed order for consistency
            let rawSubtotal = 0;
            if (detailedOrder.orderItems && detailedOrder.orderItems.length > 0) {
                rawSubtotal = detailedOrder.orderItems.reduce((sum, item) => {
                    return sum + (parseFloat(item.priceAtTimeOfOrder || 0) * parseFloat(item.quantity || 0));
                }, 0);
            }
            const pricing = calculateTotalWithFees(rawSubtotal);

            return {
                ...detailedOrder,
                subtotal: pricing.subtotal,
                platformCut: pricing.platformCut,
                supplierTotal: pricing.supplierTotal,
                deliveryFee: pricing.deliveryFee,
                deliveryPercentage: pricing.deliveryPercentage,
                // TVA field is removed
                totalAmount: pricing.total,
            };

        } catch (err) {
            showMessage("Failed to fetch order details.", 'error');
            console.error("Error fetching detailed order:", err);
            return null;
        } finally {
            setIsFetchingDetailedOrder(false);
        }
    }, [user]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders, refreshKey]);

    const totalPages = Math.ceil(totalCount / ordersPerPage);

    const showMessage = (message, type = 'success') => {
        setStatusMessage(message);
        setMessageType(type);
        setTimeout(() => setStatusMessage(null), 5000);
    };

    const handleInputChange = (e) => {
        setInputTerm(e.target.value);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setSearchTerm(inputTerm);
        setPage(1);
    };

    const handleViewOrder = async (order) => {
        // Use the simplified order if it has all the data, otherwise fetch detailed
        if (order.supplierTotal) {
            setSelectedOrder(order);
            setIsViewModalOpen(true);
        } else {
            const detailedOrder = await fetchDetailedOrder(order.orderId);
            if (detailedOrder) {
                setSelectedOrder(detailedOrder);
                setIsViewModalOpen(true);
            }
        }
    };

    const handleSuccess = (data) => {
        setRefreshKey(prev => prev + 1);
        showMessage(data.message || `Commande mise à jour avec succès!`);
    };

    
    const handleEditOrder = async (order) => {
        const detailedOrder = await fetchDetailedOrder(order.orderId);
        if (detailedOrder) {

            // --- FIX APPLIQUÉ: S'assurer que le subtotal est calculé pour l'édition si l'API le manque ---
            let calculatedRawSubtotal = parseFloat(detailedOrder.subtotal);

            // If subtotal is missing/invalid, try to calculate it from totalAmount and deliveryFee.
            if (isNaN(calculatedRawSubtotal) || calculatedRawSubtotal <= 0) {
                const total = parseFloat(detailedOrder.totalAmount);
                const delivery = parseFloat(detailedOrder.deliveryFee);

                if (!isNaN(total) && !isNaN(delivery) && total >= delivery) {
                    calculatedRawSubtotal = total - delivery;
                } else if (!isNaN(total) && total > 0) {
                    // Infer subtotal when only total is present
                    const subtotal10 = total / 1.10;
                    const subtotal6 = total / 1.06;
                    calculatedRawSubtotal = subtotal10 >= 100000 ? subtotal6 : subtotal10;
                } else if (detailedOrder.orderItems && detailedOrder.orderItems.length > 0) {
                    // Fallback: calculate subtotal from items
                    calculatedRawSubtotal = detailedOrder.orderItems.reduce((sum, item) => {
                        return sum + (parseFloat(item.priceAtTimeOfOrder) * parseFloat(item.quantity));
                    }, 0);
                }
            }

            if (calculatedRawSubtotal > 0) {
                const pricing = calculateTotalWithFees(calculatedRawSubtotal);

                detailedOrder.subtotal = pricing.subtotal;
                detailedOrder.platformCut = pricing.platformCut;
                detailedOrder.supplierTotal = pricing.supplierTotal;
                detailedOrder.deliveryFee = pricing.deliveryFee;
                detailedOrder.deliveryPercentage = pricing.deliveryPercentage;
                detailedOrder.totalAmount = pricing.total;
            }
            // --- FIN FIX ---

            setSelectedOrder(detailedOrder);
            setIsEditModalOpen(true);
        }
    };


    const handlePrintReceipt = async (order) => {
        const detailedOrder = await fetchDetailedOrder(order.orderId);
        if (!detailedOrder) {
            showMessage("Impossible de charger les détails de la commande.", 'error');
            return;
        }

        // Calculate raw subtotal from order items (base price only for suppliers)
        let rawSubtotal = 0;
        if (detailedOrder.orderItems && detailedOrder.orderItems.length > 0) {
            rawSubtotal = detailedOrder.orderItems.reduce((sum, item) => {
                return sum + (parseFloat(item.priceAtTimeOfOrder) * parseFloat(item.quantity));
            }, 0);
        }

        // Calculate all fees and totals using the updated helper (TVA-free)
        const pricing = calculateTotalWithFees(rawSubtotal);

        const subtotal = pricing.subtotal;
        const platformCut = pricing.platformCut;
        const supplierTotal = pricing.supplierTotal;
        const deliveryFee = pricing.deliveryFee;
        const deliveryPercentage = pricing.deliveryPercentage;
        // TVA removed
        const total = pricing.total; // Customer's Final Total (HT)

        const logoUrl = 'https://res.cloudinary.com/dynplclnn/image/upload/v1757705765/katabe-logo_fk0tn7.jpg';
        const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=ORDER:${detailedOrder.orderId}`;

        const itemsHtml = detailedOrder.orderItems && detailedOrder.orderItems.length > 0
            ? detailedOrder.orderItems.map((item) => `
                <tr>
                    <td style="padding: 12px; border-bottom: 1px solid #E5E7EB;">${item.name}</td>
                    <td style="padding: 12px; border-bottom: 1px solid #E5E7EB; text-align: center;">${item.quantity}</td>
                    <td style="padding: 12px; border-bottom: 1px solid #E5E7EB; text-align: right;">${formatCDF(item.priceAtTimeOfOrder)}</td>
                    <td style="padding: 12px; border-bottom: 1px solid #E5E7EB; text-align: right; font-weight: 600;">${formatCDF(item.quantity * item.priceAtTimeOfOrder)}</td>
                </tr>
            `).join('')
            : '<tr><td colspan="4" style="padding: 12px; text-align: center; color: #6B7280;">Aucun article</td></tr>';

        const orderDate = new Date(detailedOrder.createdAt || Date.now()).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      color: #1F2937;
      padding: 40px;
      background-color: #F9FAFB;
    }
    .receipt {
      max-width: 800px;
      margin: 0 auto;
      background: white;
      padding: 60px;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    }
    .header {
      text-align: center;
      margin-bottom: 40px;
      padding-bottom: 30px;
      border-bottom: 3px solid #DC2626;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .logo-image {
      width: 140px;
      height: 140px;
      object-fit: contain;
      margin-bottom: 15px;
    }
    .subtitle {
      color: #6B7280;
      font-size: 16px;
    }
    .receipt-title {
      font-size: 28px;
      font-weight: 700;
      color: #DC2626;
      margin-bottom: 30px;
      text-align: center;
    }
    .info-section {
      display: flex;
      justify-content: space-between;
      margin-bottom: 40px;
      gap: 40px;
    }
    .info-block {
      flex: 1;
    }
    .info-block h3 {
      font-size: 14px;
      font-weight: 700;
      color: #6B7280;
      text-transform: uppercase;
      margin-bottom: 12px;
      letter-spacing: 0.5px;
    }
    .info-block p {
      font-size: 15px;
      line-height: 1.6;
      color: #374151;
      margin: 4px 0;
    }
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 30px;
    }
    .items-table th {
      background-color: #F3F4F6;
      padding: 16px 12px;
      text-align: left;
      font-size: 13px;
      font-weight: 700;
      color: #374151;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border-bottom: 2px solid #DC2626;
    }
    .items-table th:nth-child(2),
    .items-table th:nth-child(3),
    .items-table th:nth-child(4) {
      text-align: center;
    }
    .items-table th:last-child {
      text-align: right;
    }
    .items-table td {
      font-size: 15px;
      color: #374151;
    }
    .totals {
      margin-left: auto;
      width: 350px;
      margin-top: 20px;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      padding: 12px 0;
      font-size: 15px;
    }
    .total-row.subtotal {
      color: #6B7280;
      border-bottom: 1px solid #E5E7EB;
    }
    .total-row.platform {
      color: #DC2626;
    }
    .total-row.supplier-net {
        font-weight: 700;
        border-bottom: 2px solid #E5E7EB;
        padding-top: 15px;
        padding-bottom: 15px;
    }
    .total-row.delivery {
      color: #6B7280;
      border-bottom: 1px solid #E5E7EB; /* Changed from 2px to 1px since TVA is removed */
    }
    /* TVA-related styles removed: .total-row.vat */
    
    .total-row.final {
      font-size: 20px;
      font-weight: 700;
      color: #DC2626;
      padding-top: 16px;
    }
    .qr-section {
      text-align: center;
      margin-top: 50px;
      padding-top: 40px;
      border-top: 2px solid #E5E7EB;
    }
    .qr-section h3 {
      font-size: 16px;
      font-weight: 700;
      color: #374151;
      margin-bottom: 20px;
    }
    .qr-section img {
      border: 3px solid #DC2626;
      border-radius: 8px;
      padding: 10px;
      background: white;
    }
    .qr-section p {
      margin-top: 15px;
      color: #6B7280;
      font-size: 14px;
    }
    .footer {
      text-align: center;
      margin-top: 50px;
      padding-top: 30px;
      border-top: 1px solid #E5E7EB;
      color: #6B7280;
      font-size: 13px;
      line-height: 1.6;
    }
    .footer strong {
      color: #DC2626;
    }
    @media print {
      body {
        padding: 0;
        background-color: white;
      }
      .receipt {
        box-shadow: none;
        padding: 40px;
      }
    }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="header">
      <img src="${logoUrl}" alt="Logo" class="logo-image" />
      <div class="subtitle">Reçu de commande officiel</div>
    </div>

    <h2 class="receipt-title">REÇU N° ${detailedOrder.orderId}</h2>

    <div class="info-section">
      <div class="info-block">
        <h3>Informations Client</h3>
        <p><strong>${detailedOrder.customerFirstName || ''} ${detailedOrder.customerLastName || 'N/A'}</strong></p>
        <p>${detailedOrder.customerEmail || 'N/A'}</p>
        <p>${detailedOrder.customerPhone || 'N/A'}</p>
        <p>${detailedOrder.deliveryAddress || 'N/A'}</p>
      </div>

      <div class="info-block">
        <h3>Détails de la commande</h3>
        <p><strong>Numéro:</strong> N° ${detailedOrder.orderId}</p>
        <p><strong>Date:</strong> ${orderDate}</p>
      </div>
    </div>

    <table class="items-table">
      <thead>
        <tr>
          <th>Produit</th>
          <th>Quantité</th>
          <th>Prix unitaire</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        ${itemsHtml}
      </tbody>
    </table>

    <div class="totals">
      <div class="total-row subtotal">
          <span>Sous-total (Produits):</span>
          <span>${formatCDF(subtotal)}</span>
      </div>
      <div class="total-row platform">
          <span>Frais de Plateforme (10%):</span>
          <span style="color: #DC2626; font-weight: 600;">-${formatCDF(platformCut)}</span>
      </div>
      <div class="total-row supplier-net">
          <span>Total Fournisseur (Net):</span>
          <span style="font-weight: 700;">${formatCDF(supplierTotal)}</span>
      </div>
      <div class="total-row delivery">
          <span>Frais de Livraison (${deliveryPercentage}%):</span>
          <span>+${formatCDF(deliveryFee)}</span>
      </div>
      
      <div class="total-row final">
          <span>Montant Total (Client HT):</span>
          <span>${formatCDF(total)}</span>
      </div>
    </div>

    <div class="qr-section">
      <h3>Code QR de livraison</h3>
      <img src="${qrCodeUrl}" alt="QR Code" width="200" height="200" />
      <p>Scannez ce code pour confirmer la livraison</p>
      <p style="font-size: 12px; margin-top: 5px;">Code: ORDER:${detailedOrder.orderId}</p>
    </div>

    <div class="footer">
      <p><strong>Merci pour votre commande!</strong></p>
      <p>Pour toute question, contactez-nous</p>
    </div>
  </div>
  <script>
    window.onload = function() {
      window.print();
    };
  </script>
</body>
</html>
        `;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(htmlContent);
            printWindow.document.close();
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-2xl text-gray-700 animate-pulse">Chargement des commandes...</div>
            </div>
        );
    }

    const getStatusStyle = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'in_transit':
                return 'bg-blue-100 text-blue-800';
            case 'delivered':
                return 'bg-green-100 text-green-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const statusMap = {
        'pending': 'En attente',
        'in_transit': 'En cours de livraison',
        'delivered': 'Livrée',
        'cancelled': 'Annulée'
    };

    return (
        <div className="min-h-screen bg-gray-100 font-sans p-4 lg:p-8 text-gray-900">
            <div className="max-w-8xl mx-auto">
                <header className="mb-8 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                    <div>
                        <h1 className="text-3xl lg:text-4xl font-extrabold text-gray-900">
                            Gestion de vos commandes 📦
                        </h1>
                        <p className="mt-2 text-md lg:text-lg text-gray-500">
                            Visualisez et gérez les commandes contenant vos produits.
                        </p>
                    </div>
                </header>

                {statusMessage && (
                    <div className={`border px-4 py-3 rounded-xl mb-4 text-sm flex items-center space-x-2 ${messageType === 'success' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-red-100 border-red-400 text-red-700'}`} role="alert">
                        {messageType === 'success' ? iconSuccess : null}
                        {messageType === 'error' ? iconDanger : null}
                        <span>{statusMessage}</span>
                    </div>
                )}

                <hr className="my-6" />

                <h2 className="text-2xl font-bold mb-4">Tableau de bord d'analyse</h2>
                <OrdersDashboard orders={orders} />

                <hr className="my-6" />

                <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
                    <form onSubmit={handleSearch}>
                        <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
                            <div className="relative flex-1 w-full">
                                {iconSearch}
                                <input
                                    type="text"
                                    value={inputTerm}
                                    onChange={handleInputChange}
                                    placeholder="Rechercher par nom du client..."
                                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all duration-200"
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full sm:w-auto px-8 py-3 bg-indigo-500 text-white font-semibold rounded-xl hover:bg-indigo-600 transition-colors"
                            >
                                Rechercher
                            </button>
                        </div>
                    </form>
                    <div className="overflow-x-auto">
                        <table className="min-w-full table-auto">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-xs sm:text-xs font-semibold">
                                    <th className="py-4 px-3 sm:px-6 text-left rounded-tl-xl">ID</th>
                                    <th className="py-4 px-3 sm:px-6 text-left">Statut</th>
                                    <th className="py-4 px-3 sm:px-6 text-left">Client</th>
                                    <th className="py-4 px-3 sm:px-6 text-left hidden md:table-cell">Livreur</th>

                                    <th className="py-4 px-3 sm:px-6 text-right hidden lg:table-cell">Frais Plat.</th>
                                    <th className="py-4 px-3 sm:px-6 text-right font-bold">Total Fourn.</th>
                                    <th className="py-4 px-3 sm:px-6 text-right hidden xl:table-cell rounded-tr-xl">Total Client</th>
                                    {/* --- END UPDATED HEADERS --- */}

                                    <th className="py-4 px-3 sm:px-6 text-center rounded-tr-xl">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="text-gray-600 text-xs font-light divide-y divide-gray-200">
                                {orders.length > 0 ? (
                                    orders.map((order) => (
                                        <tr key={order.orderId} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="py-4 px-3 sm:px-6 text-left whitespace-nowrap font-medium text-gray-800">
                                                #{order.orderId}
                                            </td>
                                            <td className="py-4 px-3 sm:px-6 text-left">
                                                <span className={`px-2 py-1 font-bold rounded-full ${getStatusStyle(order.status)}`}>
                                                    {statusMap[order.status]}
                                                </span>
                                                <span className="block text-xs text-gray-400 mt-2">
                                                    {order.updatedAt ? new Date(order.updatedAt).toLocaleString('fr-FR', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) : 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-4 px-3 sm:px-6 text-left">
                                                {order.customerFirstName} {order.customerLastName || 'N/A'}
                                                <span className="block text-xs text-gray-400 ">
                                                    {/* WhatsApp Link for Customer Phone Number */}
                                                    <a href={`https://wa.me/${order.customerPhoneNumber || ''}`} target="_blank" rel="noopener noreferrer">
                                                        {order.customerPhoneNumber || 'N/A'}
                                                    </a>
                                                </span>
                                            </td>
                                            <td className="py-4 px-3 sm:px-6 text-left hidden md:table-cell">
                                                {order.deliveryFirstName} {order.deliveryLastName || 'Non assigné'}
                                                <span className="block text-xs text-gray-400">
                                                    {/* WhatsApp Link for Delivery Phone Number */}
                                                    <a href={`https://wa.me/${order.deliveryPhoneNumber || ''}`} target="_blank" rel="noopener noreferrer">
                                                        {order.deliveryPhoneNumber || 'N/A'}
                                                    </a>
                                                </span>
                                            </td>

                                            <td className="py-4 px-3 sm:px-6 text-right whitespace-nowrap hidden lg:table-cell text-red-600 font-medium">
                                                -{formatCDF(order.platformCut || 0)}
                                            </td>
                                            <td className="py-4 px-3 sm:px-6 text-right whitespace-nowrap font-bold text-green-600">
                                                {formatCDF(order.supplierTotal || 0)}
                                            </td>
                                            <td className="py-4 px-3 sm:px-6 text-right whitespace-nowrap hidden xl:table-cell text-gray-900">
                                                {formatCDF(order.totalAmount || 0)}
                                            </td>
                                            {/* --- END UPDATED BODY CELLS --- */}

                                            <td className="py-4 px-3 sm:px-6 text-center">
                                                <div className="flex justify-center space-x-1 sm:space-x-2">
                                                    <button
                                                        onClick={() => handleViewOrder(order)}
                                                        className="p-2 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
                                                        title="Voir les détails"
                                                        disabled={isFetchingDetailedOrder}
                                                    >
                                                        {iconView}
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditOrder(order)}
                                                        className="p-2 rounded-full text-blue-500 hover:bg-blue-100 transition-colors"
                                                        title="Modifier la commande"
                                                        disabled={isFetchingDetailedOrder}
                                                    >
                                                        {iconEdit}
                                                    </button>
                                                    <button
                                                        onClick={() => handlePrintReceipt(order)}
                                                        className="p-2 rounded-full text-blue-500 hover:bg-blue-100 transition-colors"
                                                        title="Imprimer le reçu"
                                                        disabled={isFetchingDetailedOrder}
                                                    >
                                                        {iconPrint}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="10" className="py-10 text-center text-gray-500">
                                            Aucune commande trouvée.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-4 sm:space-y-0">
                        <div className="text-sm text-gray-600">
                            Affichage de {(page - 1) * ordersPerPage + 1} à {Math.min(page * ordersPerPage, totalCount)} sur {totalCount} commandes
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                                className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={page >= totalPages}
                                className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <OrderFormModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSuccess={handleSuccess}
                orderToEdit={selectedOrder}
                currentSupplierId={user?.userId}
            />
            <ViewOrderModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                order={selectedOrder}
            />
        </div>
    );
};

export default OrdersPage;